import React, { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button, Checkbox, Col, Form, Input, Modal, Row, Tooltip } from 'antd';
import { AES } from 'crypto-js';

import { CloseIcon, Hand, Info } from '../../assets/icons';
import AuthForm from '../../components/AuthForm/AuthForm';
import AuthInfo from '../../components/AuthInfo/AuthInfo';
import FloatingLabel from '../../components/FloatingLabel/FloatingLabel';
import EmailField from '../../components/SharedComponents/FormComponents/EmailField';
import PasswordField from '../../components/SharedComponents/FormComponents/PasswordField';

import { signUp } from '../../services/authApi/authApi';
import { formatePhoneNumber } from '../../utils/formatPhoneNumber';
import { preventReload } from '../../utils/preventReload';
import { toastNotify } from '../../utils/toastNotify';
import { FIRST_NAME_LENGTHS, FIRST_NAME_VALIDATION, LAST_NAME_LENGTHS, LAST_NAME_VALIDATION } from '../../utils/validationMessages';
import { alphabetSpaceRegex, onlyAlphabetRegex } from '../../utils/Validations';

import './Signup.scss';

const Signup = () => {
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [password, setPassword] = useState();
  const [referredBy, setReferredBy] = useState();
  const [viewForm, setViewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [subscribeForSMS, setSubscribeForSMS] = useState(false);
  const search = useLocation().search;
  const referralLink = new URLSearchParams(search).get('referredBy');
  const [form] = Form.useForm();
  let navigate = useNavigate();

  const handleChangeNumber = (e) => {
    const { value } = e.target;
    const phoneNumber = formatePhoneNumber(value);
    form.setFieldsValue({ phoneNumber: phoneNumber });
    setPhoneNumber(phoneNumber);
  };

  useEffect(() => {
    preventReload(form);
  }, []);

  useEffect(() => {
    if (referralLink) {
      setReferredBy(referralLink);
      form.setFieldsValue({ referredBy: referralLink });
    }
  }, [referralLink]);

  const onFinish = async (values) => {
    let { phoneNumber, password, firstName, ...payload } = values;
    const hashPassword = AES.encrypt(password, process.env.REACT_APP_PASSWORD_ENCRYPT_KEY).toString();
    const phoneNumberInp = values.phoneNumber
      ? `${values.phoneNumber.slice(1, 4)}${values.phoneNumber.slice(6, 9)}${values.phoneNumber.slice(10, 14)}`
      : '';
    if (phoneNumberInp) payload.phoneNumber = phoneNumberInp;
    if (referredBy && referredBy.trim() !== '') {
      payload.referredBy = referredBy.trim();
    }
    if (!referredBy === '' || !referredBy) {
      delete payload.referredBy;
    }
    payload.subscribeForSMS = subscribeForSMS;
    payload.password = hashPassword;
    payload.firstName = firstName?.trim() ?? '';
    localStorage.clear(); //clear local storage
    setLoading(true);
    try {
      const userData = await signUp(payload);
      if (!userData.isError) {
        const { accessToken, refreshToken = null } = userData.data;
        await localStorage.setItem('access_token_temp', accessToken);
        refreshToken && (await localStorage.setItem('refresh_token', refreshToken));
        await localStorage.setItem('referralLink', userData?.data?.user?.referralLink);
        localStorage.setItem('email', values.email);
        localStorage.setItem('phoneNo', values.phoneNumber);
        navigate('/sign-up/activation');
      } else {
        toastNotify('error', userData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='auth auth--details'>
      <AuthInfo
        className={viewForm ? 'hide' : null}
        title={<>Real estate investing for all</>}
        description={<>Get started today and begin building your wealth by investing in shares of fractionalized real estate.</>}
        note={
          <span className='underline cursor-pointer fw-600 auth__left__note' onClick={() => setDisclosureOpen(true)}>
            Company and Regulatory Disclosures
          </span>
        }
      >
        <div className='mobile-action' onClick={() => setViewForm(true)}>
          <Button className='w-100'>Get Started</Button>
        </div>
      </AuthInfo>
      <AuthForm
        className={viewForm ? 'show' : null}
        note={
          <>
            <span className='hide-mobile mr-4'>Already have an account?</span>
            <Link to='/login' className='underline fw-600'>
              Log in
            </Link>
          </>
        }
        title={
          <>
            <Hand verticalAlign='-3px' marginRight='8px' />
            Welcome to Realbricks
          </>
        }
        description={
          <>
            Enter your first and last name as it appears on your government issued ID.
            <Tooltip
              placement='bottomRight'
              title={
                <>
                  <p>A government issued ID could be your Passport, Driver’s License, National ID, or Permanent Resident Card.</p>
                  <p className='mb-0'> We are required by federal law to verify the identity of any person seeking to open a financial account.</p>
                </>
              }
            >
              {' '}
              <Info marginLeft='7px' top='2px' />{' '}
            </Tooltip>
          </>
        }
      >
        <Form
          name='signup-form'
          className='auth-form details-form'
          onFinish={onFinish}
          // onFinishFailed={onFinishFailed}
          autoComplete='off'
          form={form}
        >
          <Row gutter={24}>
            <Col span={24} md={12}>
              <FloatingLabel label='First Name' inputId='firstName' inputValue={firstName}>
                <Form.Item
                  validateTrigger='onBlur'
                  name='firstName'
                  rules={[
                    { required: true, message: FIRST_NAME_VALIDATION.REQUIRED },
                    {
                      transform: (value) => value?.trim(),
                      max: FIRST_NAME_LENGTHS.MAX,
                      message: FIRST_NAME_VALIDATION.MAX_LENGTH,
                    },
                    {
                      transform: (value) => value?.trim(),
                      min: FIRST_NAME_LENGTHS.MIN,
                      message: FIRST_NAME_VALIDATION.MIN_LENGTH,
                    },
                    {
                      pattern: alphabetSpaceRegex,
                      message: FIRST_NAME_VALIDATION.FORMAT,
                    },
                  ]}
                >
                  <Input id='firstName' value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </Form.Item>
              </FloatingLabel>
            </Col>
            <Col span={24} md={12}>
              <FloatingLabel label='Last Name' inputId='lastName' inputValue={lastName}>
                <Form.Item
                  validateTrigger='onBlur'
                  name='lastName'
                  rules={[
                    {
                      required: true,
                      message: LAST_NAME_VALIDATION.REQUIRED,
                    },
                    {
                      max: LAST_NAME_LENGTHS.MAX,
                      message: LAST_NAME_VALIDATION.MAX_LENGTH,
                    },
                    {
                      min: LAST_NAME_LENGTHS.MIN,
                      message: LAST_NAME_VALIDATION.MIN_LENGTH,
                    },
                    {
                      pattern: onlyAlphabetRegex,
                      message: LAST_NAME_VALIDATION.FORMAT,
                    },
                  ]}
                >
                  <Input id='lastName' value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Form.Item>
              </FloatingLabel>
            </Col>
          </Row>
          <FloatingLabel label='Email' inputId='email' inputValue={email}>
            <EmailField email={email} setEmail={(value) => setEmail(value)} />
          </FloatingLabel>
          <FloatingLabel label='Phone Number' inputId='phoneNumber' inputValue={phoneNumber}>
            <Form.Item name='phoneNumber' validateTrigger='onBlur'>
              <Input id='phoneNumber' type='tel' value={phoneNumber} onChange={(e) => handleChangeNumber(e)} placeholder='(XXX) XXX-XXXX' />
            </Form.Item>
          </FloatingLabel>
          <FloatingLabel label='Password (min 8 characters)' shortLabel='Password' inputId='password' inputValue={password}>
            <PasswordField autoCompletePassword={'new-password'} name={'password'} setPassword={(value) => setPassword(value)} password={password} />
          </FloatingLabel>
          <FloatingLabel label='Referred By' inputId='referredBy' inputValue={referredBy}>
            <Form.Item
              validateTrigger='onBlur'
              name='referredBy'
              rules={[
                {
                  max: 30,
                  message: 'Value should be less than 30 character',
                },
                {
                  min: 2,
                  message: 'Must be at least 2 characters',
                },
              ]}
            >
              <Input
                id='referredBy'
                value={referredBy}
                onChange={(e) => setReferredBy(e.target.value)}
                disabled={referralLink !== null || undefined}
              />
            </Form.Item>
          </FloatingLabel>
          <div className='mb-10'>
            <Checkbox checked={isAgreementChecked} onChange={(e) => setIsAgreementChecked(e.target.checked)}>
              {' '}
              <>
                I have read and agree to the Realbricks{' '}
                <Link to={{ pathname: '/legal', hash: 'Terms of use' }} className='fw-600 underline'>
                  user account agreement
                </Link>{' '}
                and{' '}
                <Link to={{ pathname: '/legal', hash: 'Privacy Policy' }} className='fw-600 underline'>
                  privacy policy
                </Link>
                .
              </>
            </Checkbox>
          </div>
          <div className='mb-40'>
            <Checkbox checked={subscribeForSMS} onChange={(e) => setSubscribeForSMS(e.target.checked)} disabled={!phoneNumber}>
              Opt-in to be the first to know about new properties. Signup for text messages for early access.
            </Checkbox>
          </div>
          <Form.Item className='auth-form__submit details-form__submit' shouldUpdate>
            {() => (
              <Button
                type='primary'
                htmlType='submit'
                className='w-100 ant-btn-primary--alt'
                loading={loading}
                disabled={
                  !firstName ||
                  !lastName ||
                  !email ||
                  !password ||
                  !isAgreementChecked ||
                  form.getFieldsError().filter(({ errors }) => errors.length).length > 0 ||
                  !isAgreementChecked
                }
              >
                Sign Up
              </Button>
            )}
          </Form.Item>
        </Form>
      </AuthForm>
      <Modal
        className='ant-modal--title ant-modal--footer-shadow'
        title={<p className='mb-0 subhead-1'>Company and Regulatory Disclosures</p>}
        open={disclosureOpen}
        onOk={() => setDisclosureOpen(false)}
        onCancel={() => setDisclosureOpen(false)}
        footer={
          <Button type='primary' className='ant-btn-primary--alt' onClick={() => setDisclosureOpen(false)}>
            Done
          </Button>
        }
        width={652}
        closeIcon={<CloseIcon />}
        centered
      >
        <p>
          {' '}
          Terra Mint Group, Corp ("Terra Mint"), as the Managing Member of Neptune REM, LLC ("Neptune") operates the RealBricks.com website (the
          "Site") and is not a broker-dealer or investment advisor. All securities related activity is conducted through Dalmore Group LLC, a
          registered broker-dealer and member of FINRA/SIPC, located at 525 Green Pl. Woodmere, NY 11598. You can review the brokercheck for Dalmore.
          An up-to-date Dalmore Form CRS is available.
        </p>
        <p>
          You should speak with your financial advisor, accountant and/or attorney when evaluating any offering. Neither Terra Mint, Neptune, nor
          Dalmore makes any recommendations or provides advice about investments, and no communication, through this website or in any other medium,
          should be construed as a recommendation for any security offered on or off this investment platform. The Site may make forward-looking
          statements. You should not rely on these statements but should carefully evaluate the offering materials in assessing any investment
          opportunity, including the complete set of risk factors that are provided as part of the offering circular for your consideration.
        </p>
        <p>
          Neptune is conducting public offerings pursuant to Regulation A, as amended, through the Site. The most current offering circular for
          Neptune is available here. Past performance is no guarantee of future results. Investments such as those on the Realbricks platform are
          speculative and involve substantial risks to consider before investing. These risks are outlined in the respective offering materials and
          including, but not limited to, illiquidity and a complete loss of capital. Key risks include, but are not limited to, limited operating
          history, reliance on key personnel, inherent risks in investing in real estate, dividends may not be received on predictable schedule and or
          at all by Investors, and lack of voting rights. Also, the adverse economic effects of the COVID-19 pandemic remain unknown and could
          materially impact this investment. An investment in an offering constitutes only an investment in a particular series and not in the
          underlying asset(s). Investors should carefully review the risks located in the respective offering materials for a more comprehensive
          discussion of risk.
        </p>
        <p>
          From time to time, Neptune may undertake "testing the waters" for additional series of securities to be offered pursuant to Regulation A.
          For offerings that have not yet been qualified, no money or other consideration is being solicited, and if sent in response, will not be
          accepted.
        </p>
        <p>NO MONEY OR OTHER CONSIDERATION IS BEING SOLICITED, AND IF SENT IN RESPONSE, WILL NOT BE ACCEPTED.</p>
        <p>
          NO OFFER TO BUY THE SECURITIES CAN BE ACCEPTED AND NO PART OF THE PURCHASE PRICE CAN BE RECEIVED UNTIL THE OFFERING STATEMENT FILED BY THE
          COMPANY WITH THE SEC HAS BEEN QUALIFIED BY THE SEC. ANY SUCH OFFER MAY BE WITHDRAWN OR REVOKED, WITHOUT OBLIGATION OR COMMITMENT OF ANY
          KIND, AT ANY TIME BEFORE NOTICE OF ACCEPTANCE GIVEN AFTER THE DATE OF QUALIFICATION.
        </p>
        <p>AN INDICATION OF INTEREST INVOLVES NO OBLIGATION OR COMMITMENT OF ANY KIND.</p>
        <p>
          AN OFFERING STATEMENT REGARDING THIS OFFERING HAS BEEN FILED WITH THE SEC. YOU MAY OBTAIN A COPY OF THE PRELIMINARY OFFERING CIRCULAR THAT
          IS PART OF THAT OFFERING STATEMENT FROM: Neptune REM, LLC 30 N. Gould St. Suite R Sheridan, WY 82801 OR{' '}
          <a href='https://www.sec.gov/Archives/edgar/data/1992001/000110465923116338/xsl1-A_X01/primary_doc.xml'>
            <b>
              <u>HERE</u>
            </b>
          </a>
        </p>
        <p>
          Investment overviews contained herein contain summaries of the purpose and the principal business terms of the investment opportunities.
          Such summaries are intended for informational purposes only and do not purport to be complete, and each is qualified in its entirety by
          reference to the more-detailed discussions contained in the respective offering circular filed with the SEC.
        </p>
        <p>
          Realbricks does not offer refunds after an investment has been made. Please review the relevant offering materials and subscription
          documentation for more information.
        </p>
        <p>
          Potential investors should not rely on any forward-looking statements regarding any investment opportunity, which is based on our beliefs
          and information currently available to us. The words “anticipate,” “believe,” “expect,” “aim,” “potential,” “design,” “target,” “intend,”
          “may,” “might,” “plan,” “estimate,” “project,” “projection,” “should,” “will,” “would,” “result” and similar expressions identify
          forward-looking statements. Such statements are subject to risks, uncertainties, and assumptions and are not guarantees of future
          performance, which may be affected by known and unknown risks, trends, uncertainties, and factors that are beyond our control. These risks
          could result in the loss of your investment. See the offering materials for detailed information.
        </p>
        <p>
          Neptune does not have a public trading market for its Series Interests. While it intends to seek a quotation on PPEX, an alternative trading
          system (ATS) operated by North Capital Investment Technology, Inc. (“PPEX”) with a view to providing our holders of Series Interests with
          potential liquidity in the form of a secondary market for their investment in our Series Interests. However, there can be no guarantee that
          a secondary market may develop, or if it does, as to the volume or pricing with respect to any secondary trading that might develop. The
          PPEX does not employ market makers to provide liquidity, unlike national securities exchanges. Until Neptune’s Series Interests are listed,
          if ever, you may not sell the company’s Series Interests. Therefore, it may be difficult for you to sell Neptune’s Series Interests at the
          time you wish to do so, if you are able to sell them at all. If you are able to sell your Series Interests, you may have to sell them at a
          substantial discount to their public offering price. Because of the illiquid nature of our Series Interests, you should purchase Neptune’s
          Series Interests only as a long-term investment and be prepared to hold them for an indefinite period of time.
        </p>
        <p>
          Property information shown has been provided from various sources, which can include the seller and/or public records. It is believed
          reliable but not guaranteed and should not be relied upon without independent verification.
        </p>
        <p>
          Disclaimer: Images, Information and Floor Plan and Measurements are approximate and are for illustrative purposes only. We make no
          guarantee, warranty or representation as to the accuracy and completeness of the floor plans, measurements and or any information provided.
          You or your advisers should conduct a careful, independent investigation of the property to determine to your satisfaction as to the
          suitability of the property for your space requirements. All pricing and availability is subject to change. The information is to be used as
          a point of reference and not a binding agreement.
        </p>
      </Modal>
    </div>
  );
};

export default Signup;
